import { Price } from '../_stores/UIDataStore';
import * as Immutable from 'immutable';
import HTMLString from '../../Common/Utils/HTMLString';

export type FabricImages = {
	front: string
	back: string
	closeup: string
	paper: string
}

export type FabricCategory = {
	header: string
	default: string
	description: string
	fabrics: string[]
}

export interface IFabricSpecs {
	content: string
	width: string
	color: string
	weight: string
	shrinkage: string
	printThrough: string
	certificate: HTMLString
} const defaultFabricSpecs = Immutable.Record<IFabricSpecs>({
	content: "",
	width: "",
	color: "",
	weight: "",
	shrinkage: "",
	printThrough: "",
	certificate: null
}); export class FabricSpecs extends defaultFabricSpecs implements IFabricSpecs {}

export interface IFabricInfo {
	title_swatch: HTMLString,
	title_display: HTMLString,
	description: HTMLString,
	printDescription: HTMLString,
	banner: string,
	specs: FabricSpecs,
	images: FabricImages,
	uses: {[key: string]: HTMLString},
} const defaultFabricInfo = Immutable.Record<IFabricInfo>({
	title_swatch: null,
	title_display: null,
	description: null,
	printDescription: null,
	banner: "",
	specs: null,
	images: null,
	uses: null,
}); export class FabricInfo extends defaultFabricInfo implements IFabricInfo {}

export interface IFabricSize {
	slug: string
	name: string
	price: Price
	width: number
	height: number
} const defaultFabricSize = Immutable.Record<IFabricSize>({
	slug: '',
	name: '',
	price: new Price(),
	width: 0,
	height: 0,
}); export class FabricSize extends defaultFabricSize implements IFabricSize {}

export interface IFabric {
	slug: string,
	name: string,
	category: string
	sizes: Immutable.Map<number, FabricSize>
	info: FabricInfo
} const defaultFabric = Immutable.Record<IFabric>({
	slug: '',
	name: '',
	category: '',
	sizes: Immutable.Map<number, FabricSize>(),
	info: null,
}); export class Fabric extends defaultFabric implements IFabric {}

export const FabricCategories:{[key:string]:FabricCategory} = {
	"Cotton": {
		header: "/static/img/pages/fabric/cotton.svg",
		default: "cotton_canvas",
		description: "Experience the softness and breathability of cotton, custom printed with vibrant, permanent reactive inks. Reactive inks bond directly with the cotton fibers, ensuring long-lasting color that won’t fade, even after repeated washing.",
		fabrics: [
			"cotton_canvas",
			"organic_cotton_hemp",
			"cotton_sateen_200tc",
			"cotton_sateen_600tc",
			"cotton_linen_canvas",
			"pebble_canvas",
			"quilting_cotton_organic",
			"cotton_double_gauze",
			"essential_cotton_linen",
			"cotton_silk",
		]
	},
	"Silk": {
		header: "/static/img/pages/fabric/silk.svg",
		default: "silk_twill",
		description: "Unveil the timeless beauty of silk, custom printed with reactive inks for bold, enduring colors. This fabric’s natural luster and soft drape enhances any design, making it perfect for creating luxurious, high-end pieces that stand out.",
		fabrics: [
			"silk_twill",
			"silk_habotai",
			"silk_charmeuse",
			"silk_chiffon",
			"silk_modal",
			"silk_cashmere",
			"cotton_silk",
		]
	},
	"Hemp": {
		header: "/static/img/pages/fabric/hemp.svg",
		default: "organic_cotton_hemp",
		description: "Embrace sustainability with durable, eco-friendly hemp fabric, custom printed with vibrant, long-lasting reactive inks. Known for its strength and breathability, hemp offers a natural, textured feel and is both breathable and strong. ",
		fabrics: [
			"organic_cotton_hemp",
		]
	},
	"Modal": {
		header: "/static/img/pages/fabric/modal.svg",
		default: "silk_modal",
		description: "Indulge in the ultra-soft touch of modal-blended fabrics, featuring vibrant custom prints with reactive inks. The smooth texture and fluid drape of modal fibers make it perfect for scarves and lightweight clothing and accessories. ",
		fabrics: [
			"silk_modal",
			"cashmere_modal",
		]
	},
	"Cashmere": {
		header: "/static/img/pages/fabric/cashemere.svg",
		default: "cashmere_modal",
		description: "Experience the ultimate in luxury with custom printed cashmere fabric, where exquisite softness meets permanent, vibrant prints. This opulent fiber provides unparalleled warmth and softness either on its own or when blended with other fibers. ",
		fabrics: [
			"cashmere_modal",
		]
	},
	"Linen": {
		header: "/static/img/pages/fabric/linen.svg",
		default: "essential_cotton_linen",
		description: "Known for its breathable and crisp texture, linen fibers have unique properties when featured solely or when blended with other fibers. Linen drapes beautifully with a distinctive texture. Reactive prints on linen fibers are permanent and enhance the natural properties of the fabric. ", 
		fabrics: [
			"essential_cotton_linen",
			"cotton_linen_canvas",
		]
	},
	/*"Woven Polyester": {
		header: "/static/img/pages/fabric/woven_polyester.svg",
		default: "heavy_satin",
		description: "Woven Polyester fabrics are synthetic fibres that have been woven on looms to create fabrics that do not stretch. Digital sublimation prints on these fabrics are true to colour and permanent.",
		//description: "Knit polyesters include all polyester based fabrics that are constructed on knitting looms resulting in stretch fabrics. These synthetic fabrics hold their shape and can be digitally printed with sublimation prints which are vibrant and long lasting.",
		fabrics: [
			"heavy_satin",
			"swimsuit_knit",
			"peachskin_jersey",
			"velveteen",
			"poly_twill",
			"poly_canvas",
			"scuba_double_knit",
			"performance_spandex",
			"poly_chiffon",
			"spun_polyester",
			"minky",
			"double_minky",						
			"silky_knit",
			"matte_crepe",
			"satin_charmeuse",
		]
	},*/
	"Polyester": {
		header: "/static/img/pages/fabric/knit_polyester.svg",
		default: "heavy_satin",
		description: "Discover the versatility of polyester fabrics printed with vibrant sublimation inks. Polyester fabrics are known for their durability and color-fastness, ensuring that your designs remain vivid and striking over time. Perfect for everything from activewear to home decor.",
		fabrics: [
			"heavy_satin",
			"swimsuit_knit",
			"peachskin_jersey",
			"velveteen",
			"poly_twill",
			"poly_canvas",
			"scuba_double_knit",
			"performance_spandex",
			"poly_chiffon",
			"spun_polyester",
			"minky",
			"double_minky",						
			"silky_knit",
			"matte_crepe",
			"satin_charmeuse",
			"glitter_performance_knit",
		]
	},
	"Vegan Leather": {
		header: "/static/img/pages/fabric/knit_polyester.svg",
		default: "crinkle_vegan_leather",
		description: "Vegan leather offers a luxurious, cruelty-free alternative to traditional leather, crafted from high-quality synthetic or plant-based materials that capture the elegance and feel of genuine leather without using animal products. This fabric brings a refined, sophisticated touch for those seeking a more ethical and eco-friendly choice.",
		fabrics: [
			"crinkle_vegan_leather",
			"fine_grain_vegan_leather",
			"pebble_vegan_leather",
		]
	}
}

const FabricInfos:{[key:string]:IFabricInfo} = {
	'cotton_canvas': {
		title_swatch: new HTMLString('<div class="fabric-family">Cotton</div> canvas'),
		title_display: new HTMLString('<div class="fabric-family">Cotton</div> canvas'),
		description: new HTMLString('Here comes the perfect cotton canvas to the rescue! Featuring a typical canvas textured surface, this 100% cotton canvas is even and smooth. The tight weave of this fabric means your print details are not lost in the texture, even though it\'s a heavy weight fabric. Cotton canvas has excellent structure so it can be used in projects that require shape and durability (think totes!). Your next high use home decor project would be a perfect match for cotton canvas!'),
		printDescription: new HTMLString('All cotton canvas fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/cotton/cotton-canvas/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% cotton",
			width: "55”/140 cm",
			color: "Off white",
			weight: "275g/m2",
			shrinkage: "2-3% in length and 4-5% in width",
			printThrough: "Low, roughly 10%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/cotton/cotton-canvas/fabric-front.jpg",
			back: "/static/img/pages/fabric/cotton/cotton-canvas/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/cotton/cotton-canvas/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/cotton/cotton-canvas/fabric-white-paper.jpg",
		},
		uses: {
			"Home decor": new HTMLString("including pillowcases, table clothes, curtains, and upholstery projects."),
			"Good for outerwear": new HTMLString("including coats and blazers."),
			"Can be used for all styles of bags and totes.": new HTMLString(""),
		}
	},
	'cotton_sateen_200tc': {
		title_swatch: new HTMLString('<div class="fabric-family">Cotton</div> Sateen<sup>200TC</sup>'),
		title_display: new HTMLString('<div class="fabric-family">Cotton</div> Sateen<sup>200TC</sup>'),
		description: new HTMLString('Similar in weight to our 600TC cotton sateen, the 200TC cotton sateen is our budget friendly alternative also available in width width. Don’t let price stop you from making the bedding of your dreams! With a matte underneath and low sheen surface, cotton sateen is one of the basics you’ll want to live with everyday.'),
		printDescription: new HTMLString('All cotton sateen fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/cotton/cotton-sateen200/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% cotton",
			width: "94.5”/240 cm",
			color: "White",
			weight: "130 g/m2",
			shrinkage: "2-3% in length and 4-5% in width",
			printThrough: "Low, roughly 25%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/cotton/cotton-sateen200/fabric-front.jpg",
			back: "/static/img/pages/fabric/cotton/cotton-sateen200/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/cotton/cotton-sateen200/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/cotton/cotton-sateen200/fabric-white-paper.jpg",
		},
		uses: {
			"Home decor": new HTMLString("including pillow shams, table clothes, duvet covers, sheets, and curtains."),
			"Clothing": new HTMLString("including dresses and childrens articles."),
			"Crafting and Quilting": new HTMLString(""),
		}
	},
	'cotton_sateen_600tc': {
		title_swatch: new HTMLString('<div class="fabric-family">Cotton</div> Sateen<sup>600TC</sup>'),
		title_display: new HTMLString('<div class="fabric-family">Cotton</div> Sateen<sup>600TC</sup>'),
		description: new HTMLString('Cotton sateen is both a beautiful and highly functional fabric. The underside is matte while the top surface is smooth with a slight sheen that enhances your print results. At a 600 thread count, this fabric is tightly woven and you can feel the quality the moment you touch it. At a 600 thread count, each fiber in this fabric is actually 4 fibers twisted together to make one strong and continuous fiber, therefore producing the high thread count and exceptional feel.<br/><br/>Cotton sateen softens with every wash making it the perfect fabric for high use home decor products like bed sheets and duvet covers. You’ll surely fall in love with our wide width cotton sateen!'),
		printDescription: new HTMLString('All cotton sateen fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/cotton/cotton-sateen600/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% cotton",
			width: "94.5”/240 cm",
			color: "Optic White",
			weight: "140g/m2",
			shrinkage: "2-3% in length and 4-5% in width",
			printThrough: "Low, roughly 25%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/cotton/cotton-sateen600/fabric-front.jpg",
			back: "/static/img/pages/fabric/cotton/cotton-sateen600/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/cotton/cotton-sateen600/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/cotton/cotton-sateen600/fabric-white-paper.jpg",
		},
		uses: {
			"Home decor": new HTMLString("including pillow shams, table clothes, duvet covers, sheets, and curtains."),
			"Clothing": new HTMLString("including dresses and childrens articles."),
			"Crafting and Quilting": new HTMLString(""),
		}
	},
	'cotton_linen_canvas': {
		title_swatch: new HTMLString('<div class="fabric-family">Cotton</div> Linen'),
		title_display: new HTMLString('<div class="fabric-family">Cotton</div> Linen Canvas'),
		description: new HTMLString('Our cotton-linen blend is a medium weight, semi-structured fabric. It has large surface fibers so the canvas weave gives texture to your prints. It is the perfect choice when making tea towels, home decor items or loose-fitting garments. This fabric is highly absorbent. This fabric is not ideal for solid prints as it has some inherent flaws from seeds and thick fibers that may show small breaks in solid colors.'),
		printDescription: new HTMLString('All cotton linen fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/cotton/cotton-linen-canvas/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "68% cotton/32% linen",
			width: "57”/145cm",
			color: "White",
			weight: "280g/m2",
			shrinkage: "2-3% in length and 4-5% in width",
			printThrough: "Low, roughly 10%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/cotton/cotton-linen-canvas/fabric-front.jpg",
			back: "/static/img/pages/fabric/cotton/cotton-linen-canvas/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/cotton/cotton-linen-canvas/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/cotton/cotton-linen-canvas/fabric-white-paper.jpg",
		},
		uses: {
			"Home decor": new HTMLString("including curtains, pillow cases, table cloths, and tea towels."),
			"Clothing": new HTMLString("including loose-fitting garments, skirts and slacks, overalls, blazers and light coats."),
			"Accessories": new HTMLString("including bags/totes, headwear, zipper pouches."),
		}
	},
	'pebble_canvas': {
		title_swatch: new HTMLString('<div class="fabric-family">Cotton Canvas</div> Pebble'),
		title_display: new HTMLString('<div class="fabric-family">Cotton</div> Pebble Canvas'),
		description: new HTMLString('Less structured than our cotton canvas, pebble canvas has a textured surface and drapes easier. This makes it a great fabric for tea towels or projects where movement is important! As a 100% cotton fabric, it has great absorbancy. Pebble canvas can be used for light-use upholsterty projects as it has not been tested for rub count.'),
		printDescription: new HTMLString('All pebble canvas fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/cotton/pebble-canvas/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% cotton",
			width: "61”/155cm",
			color: "Off White",
			weight: "285g/m2",
			shrinkage: "2-3% in length and width",
			printThrough: "Low, roughly 10%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/cotton/pebble-canvas/fabric-front.jpg",
			back: "/static/img/pages/fabric/cotton/pebble-canvas/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/cotton/pebble-canvas/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/cotton/pebble-canvas/fabric-white-paper.jpg",
		},
		uses: {
			"Bags, Jackets, heavy clothing, light-use upholstery, tea towels": new HTMLString(""),
		}
	},
	'quilting_cotton_organic': {
		title_swatch: new HTMLString('<div class="fabric-family">Organic Cotton</div> Quilting'),
		title_display: new HTMLString('<div class="fabric-family">Organic</div> Quilting Cotton'),
		description: new HTMLString('Quilting cotton is one of the fabric basics so we made sure ours is the best possible quality! Made from 100% organic cotton, our quilting cotton is perfect for quilting or clothing projects. It is easy to work with and exactly what you expect in a good quality cotton fabric.'),
		printDescription: new HTMLString('All organic quilting cotton fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/cotton/quilting-cotton-organic/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% cotton",
			width: "55”/140cm",
			color: "Soft White",
			weight: "135g/m2",
			shrinkage: "3-4% in length and width",
			printThrough: "Low, roughly 25%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/cotton/quilting-cotton-organic/fabric-front.jpg",
			back: "/static/img/pages/fabric/cotton/quilting-cotton-organic/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/cotton/quilting-cotton-organic/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/cotton/quilting-cotton-organic/fabric-white-paper.jpg",
		},
		uses: {
			"Quilting, crafts, lightweight clothing, kids clothes": new HTMLString(""),
		}
	},

	'cotton_double_gauze': {
		title_swatch: new HTMLString('<div class="fabric-family">Cotton Double</div> Gauze'),
		title_display: new HTMLString('<div class="fabric-family">Cotton</div> Double Gauze'),
		description: new HTMLString('Cotton double gauze is one of those fabrics that you just want to touch! Like it\'s name suggests, it\'s composed of 2 layers of guaze woven together in the middle It provides coverage when used in clothing, but remains light and airy. Double gauze is commonly used for swaddling blankets and is popular for baby products.'),
		printDescription: new HTMLString('All cotton double gauze fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/cotton/cotton-double-gauze/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% cotton",
			width: "57”/145cm",
			color: "Off White",
			weight: "135g/m2",
			shrinkage: "3-4% in length and width",
			printThrough: "Medium, roughly 40%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/cotton/cotton-double-gauze/fabric-front.jpg",
			back: "/static/img/pages/fabric/cotton/cotton-double-gauze/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/cotton/cotton-double-gauze/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/cotton/cotton-double-gauze/fabric-white-paper.jpg",
		},
		uses: {
			"Lightweight clothing, scarves, baby gear, layered dresses": new HTMLString(""),
		}
	},

	'silk_twill': {
		title_swatch: new HTMLString('<div class="fabric-family">Silk</div> Twill'),
		title_display: new HTMLString('<div class="fabric-family">Silk</div> Twill'),
		description: new HTMLString('Silk twill is a luxurious fabric. At 14mm, this fabric is a medium weight silk and the denser weave provides a beautiful, even surface for the ink to be absorbed. Silk twill has a matte finish so there is only a moderate shine to this fabric. The twill weave features the characteristic diagonals and is both delicate and strong. You’ll see why silk twill is the popular choice for Hermes silk scarves! The drape and dimension of silk twill will suit any high end project.'),
		printDescription: new HTMLString('All silk twill fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/silk/silk-twill/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% silk in twill weave",
			width: "52”/ 132 cm",
			color: "Eggshell white",
			weight: "120 g/m2",
			shrinkage: "2-3% in length and 4-5% in width",
			printThrough: "Moderate, roughly 50%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/silk/silk-twill/fabric-front.jpg",
			back: "/static/img/pages/fabric/silk/silk-twill/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/silk/silk-twill/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/silk/silk-twill/fabric-white-paper.jpg",
		},
		uses: {
			"Bridal & Formal wear": new HTMLString("Scarves, ties and other accessories."),
			"Silk can be an option for luxury home decor products like pillows and curtains.": new HTMLString(""),
		}
	},
	'silk_habotai': {
		title_swatch: new HTMLString('<div class="fabric-family">Silk</div> Habotai'),
		title_display: new HTMLString('<div class="fabric-family">Silk</div> Habotai'),
		description: new HTMLString('Silk habotai is a basic weave silk and one of the thinnest fabrics we offer. Due to its lightness and delicate structure, inks penetrate almost 90% of the way through the silk which means your print looks almost as vibrant from the back as the front! Since silk is priced by weight, 8mm silk habotai is our most affordable silks and yet has one of the most desirable print results with the high print through. Depending on your print, this fabric will vary in its transparency so make sure to keep this in mind for clothing projects.'),
		printDescription: new HTMLString('All silk habotai fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/silk/silk-habotai/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% silk in plain weave",
			width: "52”/132 cm",
			color: "Ivory white",
			weight: "90 g/m2",
			shrinkage: "",
			printThrough: "High. Around 80-90% depending on the ink colors",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/silk/silk-habotai/fabric-front.jpg",
			back: "/static/img/pages/fabric/silk/silk-habotai/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/silk/silk-habotai/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/silk/silk-habotai/fabric-white-paper.jpg",
		},
		uses: {
			"Bridal and formal wear.": new HTMLString("Perfect for scarves and items where you want to see the print from both sides."),
			"Semi transparent clothing": new HTMLString(""),
			"Luxury home decor": new HTMLString(""),
		}
	},

	'silk_charmeuse': {
		title_swatch: new HTMLString('<div class="fabric-family">Silk</div> Charmeuse'),
		title_display: new HTMLString('<div class="fabric-family">Silk</div> Charmeuse'),
		description: new HTMLString('Silk charmeuse is one of the best known types of silk. It is shiny on the surface and matte underneath. The high shine enhances prints and the fabric has high drapability. Silk charmeuse flows beautifully and has a liquid-like texture which makes it feel wonderful to wear and a popular choice for slips and formal dresses.'),
		printDescription: new HTMLString('All silk charmeuse fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/silk/silk-charmeuse/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% silk",
			width: "54”/137cm",
			color: "Eggshell white",
			weight: "105g/m2",
			shrinkage: "Less than 1% in length and width",
			printThrough: "Medium, roughly 50%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/silk/silk-charmeuse/fabric-front.jpg",
			back: "/static/img/pages/fabric/silk/silk-charmeuse/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/silk/silk-charmeuse/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/silk/silk-charmeuse/fabric-white-paper.jpg",
		},
		uses: {
			"Scarves, formal wear, bridal, accessories, blouses, slips": new HTMLString(""),
		}
	},

	'silk_chiffon': {
		title_swatch: new HTMLString('<div class="fabric-family">Silk</div> Chiffon'),
		title_display: new HTMLString('<div class="fabric-family">Silk</div> Chiffon'),
		description: new HTMLString('Silk chiffon is our airiest silk. It has a loose weave which makes it very lightweight with high print through. Prints on silk chiffon are almost as visible on the backside as on the front, but this also makes it more seethru so not a great choice for clothing. Chiffon has a rougher textured matte surface and high drapability.'),
		printDescription: new HTMLString('All silk chiffon fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/silk/silk-chiffon/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% silk",
			width: "54”/137cm",
			color: "Eggshell white",
			weight: "100g/m2",
			shrinkage: "Less than 1% in length and width",
			printThrough: "High, roughly 85%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/silk/silk-chiffon/fabric-front.jpg",
			back: "/static/img/pages/fabric/silk/silk-chiffon/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/silk/silk-chiffon/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/silk/silk-chiffon/fabric-white-paper.jpg",
		},
		uses: {
			"Scarves, formal wear, bridal, accessories": new HTMLString(""),
		}
	},

	'organic_cotton_hemp': {
		title_swatch: new HTMLString('<div class="fabric-family">Organic Cotton</div> Hemp'),
		title_display: new HTMLString('<div class="fabric-family">Organic</div> Cotton Hemp'),
		description: new HTMLString('Looking for a basic that’s far from basic! Organic cotton hemp feels similar in weight and structure to quilting cotton, but has a lovely composition and tighter weave. It’s a perfect twist when you want to get away from basics. The hemp fibers create small slub textures in the fabric so your print surface has enhanced dimension. It’s easy to manipulate and sew. Use it as an alternative to quilting cotton where you want ease of use but also a bit of structure.'),
		printDescription: new HTMLString('All cotton hemp fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/cotton/organic-cotton-hemp/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "76% organic cotton/24% hemp",
			width: "54”/137 cm",
			color: "Slightly off white",
			weight: "150g/m2",
			shrinkage: "2-3% in length and 4-5% in width",
			printThrough: "Low, roughly 25%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/cotton/organic-cotton-hemp/fabric-front.jpg",
			back: "/static/img/pages/fabric/cotton/organic-cotton-hemp/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/cotton/organic-cotton-hemp/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/cotton/organic-cotton-hemp/fabric-white-paper.jpg",
		},
		uses: {
			"Men's clothing": new HTMLString("including dress shirts."),
			"Women's clothing": new HTMLString("including blouses, dresses, and lightweight pants and skirts."),
			"Quilting projects": new HTMLString('and <strong>lightweight home decor</strong>.'),
			"Would be great as a lining fabric.": new HTMLString(""),
		}
	},
	'heavy_satin': {
		title_swatch: new HTMLString('<div class="fabric-family">Heavy</div> satin'),
		title_display: new HTMLString('<div class="fabric-family">Poly</div> Heavy satin'),
		description: new HTMLString('Heavy satin has a moderate-high surface sheen and sturdy construction that makes it ideal for structured formal garments and accessories. Prints on heavy satin are reflective so the color results are enhanced. Due to the tightly woven surface, satin is as durable as it is pretty! '),
		printDescription: new HTMLString('All satin fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/poly-heavy-satin/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% polyester",
			width: "58”/147 cm",
			color: "White",
			weight: "205g/m2",
			shrinkage: "none",
			printThrough: "Low, around 20%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/poly-heavy-satin/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/poly-heavy-satin/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/poly-heavy-satin/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/poly-heavy-satin/fabric-white-paper.jpg",
		},
		uses: {
			"Bridal and formal wear.": new HTMLString(""),
			"Accessories": new HTMLString("including bags and pocket squares"),
			"Can be used for lining bags and jackets.": new HTMLString(""),
			"Can be used as a sturdy banner material.": new HTMLString(""),
		}
	},
	'swimsuit_knit': {
		title_swatch: new HTMLString('<div class="fabric-family">Swimsuit</div> knit'),
		title_display: new HTMLString('<div class="fabric-family">Poly</div> Swimsuit knit'),
		description: new HTMLString('Swimsuit knit is super stretchy that retains its shape and the integrity of its print. The surface has a subtle sheen and is soft and smooth to the touch.'),
		printDescription: new HTMLString('All swimsuit knit fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/poly-swimsuit-knit/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% ecopolyester/28% spandex",
			width: "58”/147 cm",
			color: "White",
			weight: "260g/m2",
			shrinkage: "none",
			printThrough: "Low, around 20%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/poly-swimsuit-knit/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/poly-swimsuit-knit/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/poly-swimsuit-knit/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/poly-swimsuit-knit/fabric-white-paper.jpg",
		},
		uses: {
			"Stretchy apparel": new HTMLString("including swimwear, leggings and athletic wear."),
			"Swimsuit knit makes excellent compression and shape wear.": new HTMLString(""),
		}
	},
	'peachskin_jersey': {
		title_swatch: new HTMLString('<div class="fabric-family">Peachskin</div> jersey'),
		title_display: new HTMLString('<div class="fabric-family">Poly</div> Peachskin jersey'),
		description: new HTMLString('Peachskin is extremely soft to the touch, with a matte surface finish that somewhat mimics the appearance of suede. This stretchy fabric is not rigid and has a loose, casual hang, making it an excellent replacement for jersey.'),
		printDescription: new HTMLString('All peachskin jersey fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/poly-peachskin-jersey/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "95% polyester/5% spandex",
			width: "58”/147 cm",
			color: "White",
			weight: "200g/m2",
			shrinkage: "none",
			printThrough: "",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/poly-peachskin-jersey/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/poly-peachskin-jersey/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/poly-peachskin-jersey/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/poly-peachskin-jersey/fabric-white-paper.jpg",
		},
		uses: {
			"Lightweight, stretchy clothing and baby clothes.": new HTMLString(""),
			"Garments that touch the skin.": new HTMLString(""),
			"Excellent choice to use for lining.": new HTMLString(""),
			"Home decor items": new HTMLString("including sheets and pillowcases."),
		}
	},
	'velveteen': {
		title_swatch: new HTMLString('<div class="fabric-family">Poly</div> Velveteen'),
		title_display: new HTMLString('<div class="fabric-family">Poly</div> Velveteen'),
		description: new HTMLString('Velveteen is a very heavyweight polyester fabric that adds a touch of luxury to any project. Prints on this fabric have a subtle sheen and are soft to the touch. Formal wear is given enhanced elegance with Velveteen.'),
		printDescription: new HTMLString('All velveteen fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/poly-velveteen/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% polyester",
			width: "52”/132 cm",
			color: "White",
			weight: "390g/m2",
			shrinkage: "2.5%",
			printThrough: "none",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/poly-velveteen/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/poly-velveteen/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/poly-velveteen/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/poly-velveteen/fabric-white-paper.jpg",
		},
		uses: {
			"Household decor and upholstery.": new HTMLString(""),
			"Accessories": new HTMLString("including bags and shoes."),
			"Elegant dresses, suits and skirts.": new HTMLString(""),
		}
	},
	'poly_twill': {
		title_swatch: new HTMLString('<div class="fabric-family">Poly</div> Twill'),
		title_display: new HTMLString('<div class="fabric-family">Poly</div> Twill'),
		description: new HTMLString('This unique, structured fabric is known for its diagonal weave, which gives the fabric strength and durability. It holds its shape well, while still allowing the item to move and flow.'),
		printDescription: new HTMLString('All poly twill fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/poly-twill/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% polyester",
			width: "58”/147 cm",
			color: "White",
			weight: "205g/m2",
			shrinkage: "none",
			printThrough: "Low, around 20%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/poly-twill/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/poly-twill/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/poly-twill/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/poly-twill/fabric-white-paper.jpg",
		},
		uses: {
			"Home decor items": new HTMLString("including pillow cases, tote bags, wall hangings"),
			"Semi structured clothing": new HTMLString("including skirts and hats."),
		}
	},
	'poly_canvas': {
		title_swatch: new HTMLString('<div class="fabric-family">Poly</div> Canvas'),
		title_display: new HTMLString('<div class="fabric-family">Poly</div> Canvas'),
		description: new HTMLString('Poly Canvas is among our heaviest polyesters, with a distinct woven texture and a smooth, matte surface finish. It is a structured textile with high durability, making it a great choice for household decor.'),
		printDescription: new HTMLString('All poly canvas fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/poly-canvas/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% polyester",
			width: "58”/147 cm",
			color: "White",
			weight: "360g/m2",
			shrinkage: "none",
			printThrough: "none",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/poly-canvas/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/poly-canvas/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/poly-canvas/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/poly-canvas/fabric-white-paper.jpg",
		},
		uses: {
			"Home decor items": new HTMLString("including upholstery projects, pillow cases, drapes"),
			"Children’s toys and tote bags": new HTMLString(""),
		}
	},
	'scuba_double_knit': {
		title_swatch: new HTMLString('<div class="fabric-family">Double Knit</div> Scuba'),
		title_display: new HTMLString('<div class="fabric-family">Poly</div> Scuba Double Knit'),
		description: new HTMLString('Scuba double knit is a very interesting fabric! It\'s 2 layers of tightly knit fabric joined in the middle. Due to the double layer, it has both structure and softness. You can use it to create sculptural garments which makes it a good choice for costuming. Since it\'s a sturdy knit, edges do not always need to be finished as long as they are cleanly cut.'),
		printDescription: new HTMLString('All scuba double knit fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/scuba-double-knit/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "95% polyester, 5% spandex",
			width: "58”/147 cm",
			color: "Soft white",
			weight: "400g/m2",
			shrinkage: "1-2% in length and width",
			printThrough: "none",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/scuba-double-knit/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/scuba-double-knit/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/scuba-double-knit/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/scuba-double-knit/fabric-white-paper.jpg",
		},
		uses: {
			"Masks, structured clothing, bags, accessories, costumes": new HTMLString(""),
		}
	},
	'performance_spandex': {
		title_swatch: new HTMLString('<div class="fabric-family">Performance</div> Spandex'),
		title_display: new HTMLString('<div class="fabric-family">Poly</div> Performance Spandex'),
		description: new HTMLString('Our performance polyspandex is a fabric that never quits! With its high spandex content, it has excellent recovery and can be worn over and over without losing shape. The fabric naturally wicks moisture away from the body and is easy to care for. Our performance spandex is proudly made in Montreal using an ecopolyster fiber. Ecopolyester uses significantly less water and energy during production than traditional polyester.'),
		printDescription: new HTMLString('All performance spandex fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/performance-spandex/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "88% polyester, 12% spandex",
			width: "44”/112 cm",
			color: "Optic white",
			weight: "280g/m2",
			shrinkage: "1-2% in length and width",
			printThrough: "Low, roughly 10%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/performance-spandex/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/performance-spandex/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/performance-spandex/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/performance-spandex/fabric-white-paper.jpg",
		},
		uses: {
			"Leggings, activewear, bodysuits, swimwear, costumes": new HTMLString(""),
		}
	},

	'silk_modal': {
		title_swatch		: new HTMLString('<div class="fabric-family">Modal</div> Silk'),
		title_display		: new HTMLString('<div class="fabric-family">Silk</div> Modal'),
		description			: new HTMLString('The combination of modal and silk that make up this fabric is a match made in heaven! The modal is sourced from beechwood trees and when combined with silk gives the fabric strength while remaining lightweight. This fabric has a fluid drape, easy to work with, and easy to care for.'),
		printDescription	: new HTMLString('All modal silk fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner				: "/static/img/pages/fabric/silk/silk-modal/fabric-styled.jpg",
		specs: new FabricSpecs({
			content			: "85% beechwood modal/ 15% silk",
			width			: "52”/ 132 cm",
			color			: "Off-white",
			weight			: "95 g/m2",
			shrinkage		: "2-3% in length and width",
			printThrough	: "medium",
			certificate		: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front	: "/static/img/pages/fabric/silk/silk-modal/fabric-front.jpg",
			back	: "/static/img/pages/fabric/silk/silk-modal/fabric-back.jpg",
			closeup	: "/static/img/pages/fabric/silk/silk-modal/fabric-white-closeup.jpg",
			paper	: "/static/img/pages/fabric/silk/silk-modal/fabric-white-paper.jpg",
		},
		uses: {
			"Scarves and accessories, layered clothing, flowy garments"	: new HTMLString(""),
		}
	},
	'silk_cashmere': {
		title_swatch		: new HTMLString('<div class="fabric-family">Silk</div> Cashmere'),
		title_display		: new HTMLString('<div class="fabric-family">Silk</div> Cashmere'),
		description			: new HTMLString('Two wonder fibers combine to bring you modal cashmere, a fabric that is lightweight, soft, and strong. The high modal content makes this fabric strong, breathable, and highly drapable, while the cashmere fibers enhance the softness and warmth. Modal cashmere is a wonderous blend of function and luxury.'),
		printDescription	: new HTMLString('Two wonder fibers combine to bring you modal cashmere, a fabric that is lightweight, soft, and strong. The high modal content makes this fabric strong, breathable, and highly drapable, while the cashmere fibers enhance the softness and warmth. Modal cashmere is a wonderous blend of function and luxury.'),
		banner				: "/static/img/pages/fabric/silk/silk-cashmere/fabric-styled.jpg",
		specs: new FabricSpecs({
			content			: "85% modal/ 15% cashmere",
			width			: "52”/132 cm",
			color			: "light beige",
			weight			: "95 g/m2",
			shrinkage		: "2-3% in length and width",
			printThrough	: "medium",
			certificate		: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front	: "/static/img/pages/fabric/silk/silk-cashmere/fabric-front.jpg",
			back	: "/static/img/pages/fabric/silk/silk-cashmere/fabric-back.jpg",
			closeup	: "/static/img/pages/fabric/silk/silk-cashmere/fabric-white-closeup.jpg",
			paper	: "/static/img/pages/fabric/silk/silk-cashmere/fabric-white-paper.jpg",
		},
		uses: {
			"Scarves and accessories, layered clothing, flowy garments"	: new HTMLString(""),
		}
	},

	'essential_cotton_linen': {
		title_swatch		: new HTMLString('<div class="fabric-family">Essential Cotton</div>Linen'),
		title_display		: new HTMLString('<div class="fabric-family">Essential Cotton</div>Linen'),
		description			: new HTMLString('Just like the name says, this cotton linen blend is a staple! At an almost equal blend of cotton and linen, it is balanced and pleasing to the touch and has the classic linen look. The blend of linen and cotton means you get the best benefits of both fibers. Easy to work with and lightweight enough for most clothing basics, you’ll love working with this fabric.'),
		printDescription	: new HTMLString('All cotton linen fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner				: "/static/img/pages/fabric/linen/essential-cotton-linen/fabric-styled.jpg",
		specs: new FabricSpecs({
			content			: "55% cotton/ 45% linen",
			width			: "52”/132 cm",
			color			: "light natural",
			weight			: "140 g/m2",
			shrinkage		: "2-3% in length and 4-5% in width",
			printThrough	: "low",
			certificate		: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front	: "/static/img/pages/fabric/linen/essential-cotton-linen/fabric-front.jpg",
			back	: "/static/img/pages/fabric/linen/essential-cotton-linen/fabric-back.jpg",
			closeup	: "/static/img/pages/fabric/linen/essential-cotton-linen/fabric-white-closeup.jpg",
			paper	: "/static/img/pages/fabric/linen/essential-cotton-linen/fabric-white-paper.jpg",
		},
		uses: {
			"Blouses, dresses, light clothing, light accessories and home decor"	: new HTMLString(""),
		}
	},

	'cotton_silk': {
		title_swatch		: new HTMLString('<div class="fabric-family">Cotton</div>Silk'),
		title_display		: new HTMLString('<div class="fabric-family">Cotton</div>Silk'),
		description			: new HTMLString('Gorgeous, strong, and lightweight, cotton silk features the best properties of both cotton and silk! Enjoy the high breathability, stableness, and slight surface lustre of this silk cotton blend fabric. This fabric has some crispness but still drapes well which makes it perfect for summer dresses and blouses.'),
		printDescription	: new HTMLString('All cotton silk fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner				: "/static/img/pages/fabric/cotton/cotton-silk/fabric-styled.jpg",
		specs: new FabricSpecs({
			content			: "75% cotton/ 25% silk",
			width			: "52”/132 cm",
			color			: "off white",
			weight			: "95 g/m2",
			shrinkage		: "2-3% in length and width",
			printThrough	: "medium",
			certificate		: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front	: "/static/img/pages/fabric/cotton/cotton-silk/fabric-front.jpg",
			back	: "/static/img/pages/fabric/cotton/cotton-silk/fabric-back.jpg",
			closeup	: "/static/img/pages/fabric/cotton/cotton-silk/fabric-white-closeup.jpg",
			paper	: "/static/img/pages/fabric/cotton/cotton-silk/fabric-white-paper.jpg",
		},
		uses: {
			"Scarves and accessories, layered clothing, flowy garments"	: new HTMLString(""),
		}
	},

	'spun_polyester': {
		title_swatch: new HTMLString('<div class="fabric-family">Spun</div> Polyester'),
		title_display: new HTMLString('<div class="fabric-family">Spun</div> Polyester'),
		description: new HTMLString('Spun polyester fabric is a 4 way medium weight stretch fabric with a cottony feel. Made from exceptional quality spun fibers, this fabric will withstand heavy use while still looking great. Use it for casual garments or accessories.'),
		printDescription: new HTMLString('All spun polyester fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/spun-polyester/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "92% polyester/ 8% spandex",
			width: "58”/ 147 cm",
			color: "Eggshell white",
			weight: "280 g/m2",
			shrinkage: "less than 1%",
			printThrough: "Low",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/spun-polyester/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/spun-polyester/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/spun-polyester/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/spun-polyester/fabric-white-paper.jpg",
		},
		uses: {
			"Heavy weight clothing, blankets, accessories": new HTMLString(""),
		}
	},
	'silky_knit': {
		title_swatch: new HTMLString('<div class="fabric-family">Silky</div> knit'),
		title_display: new HTMLString('<div class="fabric-family">Silky</div> knit'),
		description: new HTMLString('With a smooth silky feel, this 4 way stretch performance polyester is lightweight and made to move with you. The high spandex content means this fabric has excellent recovery and will bounce back even after heavy use. It’s perfect for performance wear and layering.'),
		printDescription: new HTMLString('All silky knit fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/silky-knit/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "85% ecopolyester/ 15% spandex",
			width: "52”/132 cm",
			color: "Optic white",
			weight: "140 g/m2",
			shrinkage: "less than 1%",
			printThrough: "Medium",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/silky-knit/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/silky-knit/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/silky-knit/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/silky-knit/fabric-white-paper.jpg",
		},
		uses: {
			"Light clothing, swimwear (with lining) performance wear, draped clothing": new HTMLString(""),
		}
	},

	'minky': {
		title_swatch: new HTMLString('Minky'),
		title_display: new HTMLString('<div class="fabric-family">Minky</div>'),
		description: new HTMLString('With a pile height of 2mm, this luxurious single-sided minky fabric feels and prints wonderfully. Use it for projects you want to snuggle up to or where added warmth is your goal. Blankets in this fabric are a must!'),
		printDescription: new HTMLString('All minky fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/minky/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% polyester",
			width: "54”/ 137 cm",
			color: "Soft white",
			weight: "300 g/m2",
			shrinkage: "less than 1%",
			printThrough: "None",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/minky/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/minky/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/minky/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/minky/fabric-white-paper.jpg",
		},
		uses: {
			"Blankets, winter wear, accessories": new HTMLString(""),
		}
	},
	
	'double_minky': {
		title_swatch: new HTMLString('<div class="fabric-family">Double</div> Minky'),
		title_display: new HTMLString('<div class="fabric-family">Double</div> Minky'),
		description: new HTMLString('Double minky is the perfect fabric for projects that require extra comfort! Featuring solid grey minky on the back and a printable white layer on the front, double minky is a game changer for blankets. It’s heavy and warm due to the two layers of fabric fused together. Double minky is highly durable and can be washed over and over without losing it’s magical softness.'),
		printDescription: new HTMLString('All double minky fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/double-minky/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% polyester",
			width: "54”/ 137 cm",
			color: "Optic white",
			weight: "550 g/m2",
			shrinkage: "less than 1%",
			printThrough: "None",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/double-minky/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/double-minky/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/double-minky/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/double-minky/fabric-white-paper.jpg",
		},
		uses: {
			"Blankets, winter wear, accessories": new HTMLString(""),
		}
	},

	'matte_crepe': {
		title_swatch: new HTMLString('<div class="fabric-family">Matte</div> Crepe'),
		title_display: new HTMLString('<div class="fabric-family">Matte</div> Crepe'),
		description: new HTMLString('Matte crepe is a shine-free silk-like fabric that can be used for projects that demand flow and movement. It is opaqe enough for clothing but still lightweight enough to feel airy and comfortable. It’s an economical stand in for silk.'),
		printDescription: new HTMLString('All matte crepe fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/matte-crepe/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% polyester",
			width: "55”/140 cm",
			color: "Bright white",
			weight: "90 g/m2",
			shrinkage: "less than 1%",
			printThrough: "Medium",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/matte-crepe/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/matte-crepe/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/matte-crepe/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/matte-crepe/fabric-white-paper.jpg",
		},
		uses: {
			"Light clothing, scarves and accesories, flowy garments, lining": new HTMLString(""),
		}
	},

	'poly_chiffon': {
		title_swatch: new HTMLString('<div class="fabric-family">Poly</div> Chiffon'),
		title_display: new HTMLString('<div class="fabric-family">Poly</div> Chiffon'),
		description: new HTMLString('A favorite for scarves, polychiffon is a lightweight semi sheer fabric with a high print through. You’ll see your print almost 80% through to the back side. Polychiffon has a matte surface and beautiful drape which makes it a perfect match for high end projects.'),
		printDescription: new HTMLString('All polyester chiffon fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/poly-chiffon/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% polyester",
			width: "54”/137 cm",
			color: "Matt White",
			weight: "100 g/m2",
			shrinkage: "less than 1%",
			printThrough: "High",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/poly-chiffon/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/poly-chiffon/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/poly-chiffon/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/poly-chiffon/fabric-white-paper.jpg",
		},
		uses: {
			"Light clothing, scarves and accesories, flowy garments": new HTMLString(""),
		}
	},

	'satin_charmeuse': {
		title_swatch: new HTMLString('<div class="fabric-family">Satin</div> Charmeuse'),
		title_display: new HTMLString('<div class="fabric-family">Satin</div> Charmeuse'),
		description: new HTMLString('If you’re looking for a vegan silk, look no further! Satin charmeuse is a lightweight flowy fabric with a high sheen that mimics silk charmeuse. It has fluid drape and prints are enhanced by the satiny surface which makes it perfect for formalwear, scarves, and lining.'),
		printDescription: new HTMLString('All satin charmeuse fabrics are printed on-demand with our vibrant and permanent sublimation inks.'),
		banner: "/static/img/pages/fabric/polyester/satin-charmeuse/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "100% polyester",
			width: "58”/147 cm",
			color: "Bright white",
			weight: "95 g/m2",
			shrinkage: "less than 1%",
			printThrough: "Medium",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/satin-charmeuse/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/satin-charmeuse/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/satin-charmeuse/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/satin-charmeuse/fabric-white-paper.jpg",
		},
		uses: {
			"Light clothing, scarves and accesories, flowy garments, lining": new HTMLString(""),
		}
	},

	'cashmere_modal': {
		title_swatch: new HTMLString('<div class="fabric-family">Modal</div> Cashmere'),
		title_display: new HTMLString('<div class="fabric-family">Cashmere</div> Modal'),
		description: new HTMLString('Two wonder fibers combine to bring you modal cashmere, a fabric that is lightweight, soft, and strong. The high modal cotent makes this fabric fabric strong, breathable, and highly drapable, while the cashmere fibers enhance the softness and warmth. Modal cashmere is a wonderous blend of function and luxury.'),
		printDescription: new HTMLString('All modal cashmere fabrics are printed on-demand with our vibrant and permanent reactive inks.'),
		banner: "/static/img/pages/fabric/modal/cashmere-modal/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "85% modal/ 15% cashmere",
			width: "52”/ 132 cm",
			color: "Light beige",
			weight: "95g/m2",
			shrinkage: "2-3% in length and width",
			printThrough: "Medium",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/modal/cashmere-modal/fabric-front.jpg",
			back: "/static/img/pages/fabric/modal/cashmere-modal/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/modal/cashmere-modal/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/modal/cashmere-modal/fabric-white-paper.jpg",
		},
		uses: {
			"Scarves and accessories, layered clothing, flowy garments": new HTMLString(""),
		}
	},

	'glitter_performance_knit': {
		title_swatch: new HTMLString('<div class="fabric-family">Glitter Performance</div> Knit'),
		title_display: new HTMLString('<div class="fabric-family">Glitter</div> Performance Knit'),
		description: new HTMLString('Glitter performance knit is a stretch fabric that combines the versatility of performance fabrics with a whole lot of glamor! The glitter foil finish reflects light and enhances print details. This fabric has a high spandex content with excellent recovery. The glitter finish is highly durable and will withstand frequent use and washing.'),
		printDescription: new HTMLString('All glitter performance knit fabrics are printed on-demand with our vibrant and permanent sublimation inks. '),
		banner: "/static/img/pages/fabric/polyester/glitter-performance-knit/fabric-styled.jpg",
		specs: new FabricSpecs({
			content: "88% polyester/12% spande",
			width: "58”/147cm",
			color: "Glitter silver/light grey",
			weight: "295 g/m2",
			shrinkage: "none",
			printThrough: "Low 10%",
			certificate: new HTMLString('<a href="#">Download</a>'),
		}),
		images: {
			front: "/static/img/pages/fabric/polyester/glitter-performance-knit/fabric-front.jpg",
			back: "/static/img/pages/fabric/polyester/glitter-performance-knit/fabric-back.jpg",
			closeup: "/static/img/pages/fabric/polyester/glitter-performance-knit/fabric-white-closeup.jpg",
			paper: "/static/img/pages/fabric/polyester/glitter-performance-knit/fabric-white-paper.jpg",
		},
		uses: {
			"Dance wear including bodysuits and leggings, accessories, activewear and sportswear": new HTMLString(""),
		}
	},
	'pebble_vegan_leather': {
		specs: new FabricSpecs({
			content: "10% Polyurethane, 50% PVC, 40% polyester",
			width: "52”/ 132 cm",
			weight: "250 g/m2",
		}),
	},
	'fine_grain_vegan_leather': {
		specs: new FabricSpecs({
			content: "55% Polyester, 45% Polyurethane",
			width: "52”/ 132 cm",
			weight: "145 g/m2",
		}),
	},
	'crinkle_vegan_leather': {
		specs: new FabricSpecs({
			content: "5% Polyurethane, 30% PVC, 15% Polyester, 25% Plasticizers, 25% Calcium Powder",
			width: "52”/ 132 cm",
			weight: "105 g/m2",
		}),
	}
};

export default FabricInfos;